import { useStackPageAugustRevisionEnabled } from "@mirage/august-revision-hook";
import { StackSortOption } from "@mirage/service-settings/service/types";
import { useStackByIdSortSettings } from "@mirage/service-settings/useStackByIdSortSettings";
import {
  activeStackSearchItemsAtom,
  activeStackSectionsAtom,
} from "@mirage/stacks/ActiveStack/atoms";
import { useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { SearchResultsStackItemsSection } from "./SearchResultsStackItemsSection";
import { SectionArea } from "./SectionArea";
import { SortedStackItemsSection } from "./SortedStackItemsSection";

import type { stacks } from "@dropbox/api-v2-client";
import { activeStackHasWritePermissionsAtom } from "@mirage/stacks/ActiveStack/atoms";
import i18n from "@mirage/translations";
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from "react-beautiful-dnd";
import { useSectionsDragDrop } from "./DragDrop";

interface SectionsProps {
  namespaceId: string;
  onEditItem: (item: stacks.StackItemShortcut) => void;
  // this override flag is used to display the dfb august revision styles
  // when we want to render the new styles but can't feature gate users
  // ex. public view of the stacks page using (dfi styles, and we wanted to display dfb styles)
  alwaysUseAugustRevisionDfBStyles?: boolean;
}

export const Sections: React.FC<SectionsProps> = ({
  namespaceId,
  onEditItem,
  alwaysUseAugustRevisionDfBStyles,
}) => {
  const augustRevision = Boolean(
    useStackPageAugustRevisionEnabled() || alwaysUseAugustRevisionDfBStyles,
  );
  const { defaultSection } = useAtomValue(activeStackSectionsAtom);
  const { onDrop, otherSections } = useSectionsDragDrop();
  const { sortPreference } = useStackByIdSortSettings(namespaceId);
  const searchResults = useAtomValue(activeStackSearchItemsAtom);
  const hasWritePermissions = useAtomValue(activeStackHasWritePermissionsAtom);

  const [localSections, setLocalSections] = useState<stacks.Section[]>(
    otherSections || [],
  );

  useEffect(() => {
    setLocalSections(otherSections || []);
  }, [otherSections]);

  if (augustRevision && searchResults) {
    return (
      <SearchResultsStackItemsSection
        items={searchResults}
        onEditItem={onEditItem}
      />
    );
  }

  if (
    sortPreference !== undefined &&
    sortPreference.option !== StackSortOption.SECTION
  ) {
    return <SortedStackItemsSection onEditItem={onEditItem} />;
  }

  const handleDragEnd = (result: DropResult) => {
    // If the section was dropped outside of the list, do nothing
    if (!result.destination) {
      return;
    }

    const fromIndex = result.source.index;
    const toIndex = result.destination.index;

    // If the section was dropped in the same position, do nothing
    if (fromIndex === toIndex) {
      return;
    }

    // Reorder the sections based on the drag-and-drop result
    const reorderedSections = Array.from(otherSections || []);
    const [removed] = reorderedSections.splice(fromIndex, 1);
    reorderedSections.splice(toIndex, 0, removed);

    // Update the sections in global state & on the server
    onDrop(
      reorderedSections.map((section) => ({
        id: section.id ?? "",
      })),
    );

    // Optimistically update the local state to reflect the new order
    setLocalSections(reorderedSections);
  };

  return (
    <>
      {defaultSection && (
        <SectionArea
          section={defaultSection}
          onEditItem={onEditItem}
          alwaysUseAugustRevisionDfBStyles={alwaysUseAugustRevisionDfBStyles}
        />
      )}
      {otherSections?.length !== 0 && (
        // We use react-beautiful-dnd here directly, rather than the existing DragDropList
        // component, because we need finer control over the draggable component.
        <DragDropContext
          dragHandleUsageInstructions={i18n.t("drag_handle_usage_instructions")}
          onDragEnd={handleDragEnd}
        >
          <Droppable
            droppableId="stackSections"
            isDropDisabled={!hasWritePermissions}
          >
            {(droppableProvided) => (
              <div
                ref={droppableProvided.innerRef}
                {...droppableProvided.droppableProps}
              >
                {localSections.map((section, index) => (
                  <Draggable
                    draggableId={section.id ?? ""}
                    index={index}
                    key={section.id ?? ""}
                  >
                    {(provided) => (
                      <SectionArea
                        key={section.id}
                        section={section}
                        onEditItem={onEditItem}
                        alwaysUseAugustRevisionDfBStyles={
                          alwaysUseAugustRevisionDfBStyles
                        }
                        draggableProps={provided}
                      />
                    )}
                  </Draggable>
                ))}
                {droppableProvided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </>
  );
};
